import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData, selectionsData } from "@/data";
import { getTracksListQuery } from "@/graph";
import { openAlert, setReleaseTracks, setSelection } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const buildTracksVariables = (isPaginated, limit, page, importerData, filters, releaseId, searchValue) => ({
    ...(isPaginated && {
        limit,
        page: page - 1,
    }),
    orderBy: { track_num: "asc" }, //eslint-disable-line
    where: {
        ...(!importerData && {
            AND: [
                {
                    ...(((filters?.releases && Object.keys(filters?.releases).length > 0) || releaseId) && {
                        releaseTracks: {
                            ...(filters?.releases &&
                                Object.keys(filters?.releases).length > 0 && {
                                    every: {
                                        release: {
                                            ...filters?.releases,
                                            artist: {},
                                        },
                                    },
                                }),
                            ...(releaseId && {
                                some: {
                                    releaseId: { equals: releaseId },
                                },
                            }),
                        },
                    }),
                    ...(filters?.tracks &&
                        Object.keys(filters?.tracks).length > 0 && {
                            ...(filters?.tracks?.duration && {
                                trackFile: { duration: filters?.tracks?.duration },
                            }),
                            ...(filters?.tracks?.isrc && { isrc: filters?.tracks?.isrc }),
                        }),
                    ...(filters?.videos &&
                        Object.keys(filters?.videos).length > 0 && {
                            NOT: {
                                videos: { none: {} },
                            },
                        }),
                    ...(filters?.releases?.artist?.id && { artist: filters?.releases?.artist }),
                },
            ],
        }),
        ...(importerData && {
            OR: [
                {
                    ...(importerData && {
                        isrc: { in: importerData.isrcsList },
                    }),
                },
            ],
        }),
        ...(searchValue && {
            title: { contains: searchValue },
        }),
    },
});

const useTracksList = (
    isPaginated = true,
    hasAutoCompleteMenu = false,
    filters = {},
    importerData,
    releaseId,
    searchValue,
) => {
    const [params] = useSearchParams();

    const pageParam = parseInt(params.get("page")) || 1;

    const limitParam = parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const [cancelRequest, setCancelRequest] = useState(false);

    const [fetched, setFetched] = useState(false);

    const dispatchedDataRef = useRef(null);

    const isrcsListRef = useRef(importerData?.isrcsList);

    if (importerData?.isrcsList?.length > 0 && importerData?.isrcsList !== isrcsListRef.current) {
        isrcsListRef.current = importerData?.isrcsList;

        dispatchedDataRef.current = null;
    }

    const releaseRef = useRef(importerData?.release);

    if (importerData?.release && importerData?.release !== releaseRef.current) {
        releaseRef.current = importerData?.release;

        dispatchedDataRef.current = null;
    }

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const selections = useSelector((state) => state.selectionsReducer);

    const { isAuthenticated } = useAuth0();

    const { tracks: tracksKey } = selectionsData.keys;

    const { data, error, loading, refetch } = useQuery(getTracksListQuery, {
        skip: !isAuthenticated || (importerData && importerData.isrcsList?.length === 0) || cancelRequest,
        variables: buildTracksVariables(isPaginated, limit, page, importerData, filters, releaseId, searchValue),
    });

    if (data?.listTracks && dispatchedDataRef.current !== data) {
        dispatchedDataRef.current = data;

        if (!isPaginated || hasAutoCompleteMenu) {
            const fetchedList = data.listTracks.posts?.map(({ id, title }) => ({
                label: title,
                value: id,
            }));

            dispatch(
                setSelection({
                    [tracksKey]:
                        selections?.[tracksKey]?.length > 0 && hasAutoCompleteMenu
                            ? selections?.[tracksKey].concat(fetchedList)
                            : fetchedList,
                }),
            );
        }

        setFetched(true);
    }

    useEffect(() => {
        if (importerData?.stopping) setCancelRequest(false);
    }, [importerData?.stopping]);

    useEffect(
        () => {
            // `data` is undefined while the query refetches, and dispatching that would blank
            // every track dropdown bound to this list
            if (releaseId && data?.listTracks?.posts) {
                const tracks = data.listTracks.posts.map((track) => ({
                    ...track,
                    releaseTrackId: track.releaseTracks.find((releaseTrack) => releaseTrack?.releaseId === releaseId)
                        ?.id,
                }));

                dispatch(setReleaseTracks(tracks));
            }
        },
        [data], // eslint-disable-line
    );

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                if (importerData) {
                    dispatch(
                        importerData?.setInvalidImporterRelease({
                            isNetworkFailed: true,
                            originalData: importerData?.originalData,
                            release: {
                                data: importerData?.release,
                                message: t("messages.networkFailure"),
                            },
                        }),
                    );
                } else {
                    displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
                }
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        cancelRequest: () => setCancelRequest(true),
        count: data?.listTracks?.meta?.nodeCount,
        data: data?.listTracks.posts,
        fetched,
        limit,
        loading,
        page: data?.listTracks?.meta?.currentPage,
        refetch,
        searchKey: "title",
        setFetched,
        setLimit,
        setPage,
    };
};

export default useTracksList;
